import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useLanguage } from '../i18n/LanguageContext';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';

const HeroSection = styled.section`
  padding: 5rem 2rem 4rem;
  max-width: 1400px;
  margin: 0 auto;
  min-height: 90vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  
  @media (max-width: 1024px) {
    padding: 4rem 1.5rem 2rem;
  }
`;

const HeroContent = styled.div`
  width: 100%;
  max-width: 800px;
  text-align: center;
  margin-bottom: 4rem;
`;

const HeroTitle = styled.h1`
  font-size: 3.5rem;
  color: #2D3748;
  margin-bottom: 1.5rem;
  line-height: 1.2;
  font-weight: 700;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroDescription = styled.p`
  font-size: 1.25rem;
  color: #4A5568;
  margin-bottom: 2rem;
  line-height: 1.6;
  max-width: 800px;
  margin: 0 auto;
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-top: 2rem;
`;

const Button = styled.a`
  display: inline-block;
  padding: 0.75rem 1.5rem;
  border-radius: 4px;
  text-decoration: none;
  font-weight: 500;
  transition: all 0.3s ease;
`;

const PrimaryButton = styled(Button)`
  background: #3182CE;
  color: #fff;
  
  &:hover {
    background: #2B6CB0;
  }
`;

const CarouselContainer = styled.div`
  width: 100%;
  max-width: 1000px;
  position: relative;
  margin: 0 auto;
`;

const Slide = styled.div<{ active: boolean }>`
  display: ${props => props.active ? 'block' : 'none'};
  opacity: ${props => props.active ? 1 : 0};
  transition: opacity 0.5s ease-in-out;
`;

const SlideContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ImageContainer = styled.div`
  width: 100%;
  background: #f5f5f5;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  
  img {
    width: 100%;
    height: auto;
    display: block;
  }
`;

const SlideDescription = styled.p`
  font-size: 1.1rem;
  color: #4A5568;
  margin-top: 1.5rem;
  text-align: center;
  font-weight: 500;
`;

const CarouselButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: white;
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #3182CE;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  z-index: 10;
  transition: all 0.3s ease;
  
  &:hover {
    background: #f8f9fa;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
  }
  
  &:focus {
    outline: none;
  }
  
  @media (max-width: 768px) {
    width: 35px;
    height: 35px;
  }
`;

const PrevButton = styled(CarouselButton)`
  left: -20px;
  
  @media (max-width: 768px) {
    left: -10px;
  }
`;

const NextButton = styled(CarouselButton)`
  right: -20px;
  
  @media (max-width: 768px) {
    right: -10px;
  }
`;

const DotsContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 20px;
  gap: 8px;
`;

const Dot = styled.div<{ active: boolean }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${props => props.active ? '#3182CE' : '#CBD5E0'};
  cursor: pointer;
  transition: background-color 0.3s ease;
`;

const Hero: React.FC = () => {
  const { t, language } = useLanguage();
  const [activeSlide, setActiveSlide] = useState(0);

  // 定义轮播图数据
  const slides = [
    {
      image: '/markdown.png',
      description: language === 'zh' ? 'Markdown 格式预览和渲染' : 'Markdown Format Preview and Rendering'
    },
    {
      image: '/json.png',
      description: language === 'zh' ? 'JSON 数据格式化显示' : 'JSON Data Formatting Display'
    },
    {
      image: '/link.png',
      description: language === 'zh' ? '链接预览和富文本渲染' : 'Link Preview and Rich Text Rendering'
    },
    {
      image: '/latex.png',
      description: language === 'zh' ? 'LaTeX 数学公式支持' : 'LaTeX Math Formula Support'
    }
  ];

  // 自动轮播
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    
    return () => clearInterval(interval);
  }, [slides.length]);

  const nextSlide = () => {
    setActiveSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setActiveSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const goToSlide = (index: number) => {
    setActiveSlide(index);
  };

  return (
    <HeroSection id="hero">
      <HeroContent>
        <HeroTitle>{t('hero.title')}</HeroTitle>
        <HeroDescription>
          {t('hero.description')}
        </HeroDescription>
        <ButtonGroup>
          <PrimaryButton href="https://chromewebstore.google.com/detail/sheetmate/dkfanpiecenohobfaaceodidacdonlam">{t('hero.primaryButton')}</PrimaryButton>
        </ButtonGroup>
      </HeroContent>

      <CarouselContainer>
        <PrevButton onClick={prevSlide}>
          <FaChevronLeft />
        </PrevButton>
        
        {slides.map((slide, index) => (
          <Slide key={index} active={index === activeSlide}>
            <SlideContent>
              <ImageContainer>
                <img src={slide.image} alt={`Format Preview ${index + 1}`} />
              </ImageContainer>
              <SlideDescription>{slide.description}</SlideDescription>
            </SlideContent>
          </Slide>
        ))}
        
        <NextButton onClick={nextSlide}>
          <FaChevronRight />
        </NextButton>
        
        <DotsContainer>
          {slides.map((_, index) => (
            <Dot 
              key={index} 
              active={index === activeSlide} 
              onClick={() => goToSlide(index)} 
            />
          ))}
        </DotsContainer>
      </CarouselContainer>
    </HeroSection>
  );
};

export default Hero; 